// The attribute-set property panel (prototype `propsSet`): its name, colour,
// where it shows on the screen, who gets it, and — for the transaction
// designer — what an organization may change on it (tenant_may).

import { Grid2x2, List, Lock } from 'lucide-react'
import { canEdit, editableLeaves, formOf, isStandard, ruleText, SET_TENANT_MAY_KEYS, WHEN_ALWAYS, whenText } from './model'
import { opSet, useSetup } from './state'
import { setCover } from './coverage'
import { useValuesByDriver } from './StepCombinations'
import { HintBox, OriginPills, Seg, SET_COLOR_TOKENS, SetDot, StateBox } from './ui'
import TenantMayChips from './TenantMayChips'

export const SET_LEAF_LABELS: Record<string, string> = {
  label: 'Name', color: 'Colour', placement: 'Placement', rules: 'Who gets it', hidden: 'Hide',
}

export default function SetPanel() {
  const { forms, formNo, draft, node, go, labelOf, mode } = useSetup()
  const form = formOf(draft.layer, formNo)
  const setup = forms.find(f => f.formNo === formNo)
  const set = node.t === 'set' || node.t === 'col' ? form?.sets.find(s => s.key === node.set) : null
  const { values } = useValuesByDriver(formNo)
  if (!form || !set) return <div className="uds-pbody"><div className="uds-na">Pick an attribute set.</div></div>
  const leaves = editableLeaves(mode, set, SET_TENANT_MAY_KEYS)
  const can = (k: string) => canEdit(leaves, k)
  const lab = labelOf(formNo)
  const cov = setCover(form, set.key, values)
  const inside = !!set.placement?.group
  const std = isStandard(set) && mode === 'tenant'
  const edit = (fn: Parameters<typeof opSet>[2]) => draft.edit(opSet(formNo, set.key, fn))
  const allowedText = leaves ? [...leaves].map(k => SET_LEAF_LABELS[k] || k).join(', ').toLowerCase() : ''
  return (
    <>
      <div className="uds-props-h">
        <div className="row"><span className="uds-pill uds-p-acc">Attribute set</span><OriginPills standard={isStandard(set)} /></div>
        <h2>{set.label || set.key}</h2>
        <div className="sub" style={{ fontFamily: 'inherit' }}>{whenText(form.drivers, set, lab)}</div>
      </div>
      <div className="uds-pbody">
        {std && (
          <HintBox icon={<Lock size={15} />} style={{ margin: '0 0 10px' }}>
            Standard set from the application. {allowedText ? `You may change its ${allowedText}.` : 'Its settings are fixed; you may add columns to it.'}
          </HintBox>
        )}
        <label className="uds-lbl" htmlFor="uds-sname">Name</label>
        <input id="uds-sname" className="uds-inp" value={set.label || ''} disabled={!can('label')} placeholder={set.key}
          onChange={e => { const v = e.target.value; edit(s => { s.label = v }) }} />
        <div className="uds-hint uds-mono">Key: {set.key}</div>
        <label className="uds-lbl">Colour</label>
        <div className="uds-chips">
          {SET_COLOR_TOKENS.map((_, i) => (
            <button key={i} type="button" className={`uds-vchip ${Number(set.color ?? 0) % SET_COLOR_TOKENS.length === i ? 'on' : ''}`}
              disabled={!can('color')} aria-label={`Colour ${i + 1}`} onClick={() => edit(s => { s.color = i })}>
              <SetDot color={i} size={12} />
            </button>
          ))}
        </div>
        <label className="uds-lbl">Placement on the screen</label>
        <Seg value={inside ? 'in' : 'own'} disabled={!can('placement')} ariaLabel="Placement"
          onChange={v => edit(s => { s.placement = { group: v === 'in' ? (setup?.groups[0] || null) : null } })}
          options={[{ value: 'own', label: 'Its own group' }, { value: 'in', label: 'Inside a form group' }]} />
        {inside && (
          <select className="uds-inp" style={{ marginTop: 8 }} value={set.placement?.group || ''} disabled={!can('placement')}
            onChange={e => { const g = e.target.value; edit(s => { s.placement = { group: g || null } }) }}>
            {(setup?.groups || []).map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        )}
        {(mode === 'design' || can('hidden')) && (
          <div style={{ marginTop: 12 }}>
            <StateBox checked={!!set.hidden} disabled={!can('hidden')} onChange={v => edit(s => { s.hidden = v })}>
              Hide this set (it is not asked)
            </StateBox>
          </div>
        )}
        <label className="uds-lbl">Applies when</label>
        <div style={{ display: 'grid', gap: 6 }}>
          {(set.rules || []).map((r, i) => (
            <div key={i} className="uds-li" style={{ marginTop: 0 }}>
              <span className="t">{i ? 'or ' : ''}{ruleText(form.drivers, r, lab) || (i ? 'any value' : WHEN_ALWAYS)}</span>
            </div>
          ))}
          {!(set.rules || []).length && <span className="uds-na">Nobody yet</span>}
        </div>
        <div className="uds-hint">Covers {cov.covered.toLocaleString()} of {cov.all.toLocaleString()} combinations.</div>
        <div style={{ marginTop: 8 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => go({ t: 'combos' })}><Grid2x2 size={14} /> Change in Combinations</button>
        </div>
        <HintBox icon={<List size={15} />}>{(set.columns || []).length} columns. Select one in the list to set its properties.</HintBox>
        {mode === 'design' && (
          <TenantMayChips value={set.tenant_may || []} vocabulary={SET_TENANT_MAY_KEYS} labels={SET_LEAF_LABELS}
            onChange={v => edit(s => { s.tenant_may = v })} what="this set" />
        )}
      </div>
    </>
  )
}
